import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listExamResults, listExamMarksForClass } from "../../api/examApi";
import "../Attendance/Attendance.css";
import "./Marksheet.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Inline equivalent of digicampusbd's `exam-general-marksheet-download` /
 * `exam-general-marksheet-print` ("Marksheet Download"/"Generate Maksheet")
 * — one student's full mark breakdown + overall result, matching the shape
 * of the old page's own `GeneralMarksheetResponse` (student header info,
 * per-subject marks, overall total/grade/GPA/position/remarks). The old
 * page's real "download" is a Jasper PDF
 * (`/jasper/general/marksheet/download`); per this project's established
 * pattern this becomes a browser-printable page instead (`window.print()`)
 * rather than generating a PDF file — built entirely from the same
 * `GET /api/exam-results` + `GET /api/exam-marks/for-class` calls the
 * tabulation sheet uses, no new backend endpoint.
 *
 * Documented simplification (same as the tabulation sheet): shows each
 * subject's already-aggregated total/grade rather than re-deriving the
 * per-short-code breakdown a second time; also doesn't include the old
 * page's attendance-day columns (total working/present/absent/leave days),
 * which depend on a materialized attendance-summary table not migrated in
 * an earlier phase (see MIGRATION.md's Phase 7 notes on `ExamResultService`).
 */
export default function ExamMarksheetPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [identificationId, setIdentificationId] = useState("");

  const [results, setResults] = useState([]);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId || !classConfigurationId || !academicYear) {
      setResults([]);
      setMarks([]);
      return;
    }
    setLoading(true);
    setError("");
    let cancelled = false;
    Promise.all([listExamResults(examConfigId), listExamMarksForClass(examConfigId, classConfigurationId, academicYear)])
      .then(([r, m]) => {
        if (!cancelled) {
          setResults(r.filter((row) => String(row.classConfigurationId) === classConfigurationId && row.academicYear === academicYear));
          setMarks(m);
        }
      })
      .catch(() => setError("Failed to load marksheet data."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [examConfigId, classConfigurationId, academicYear]);

  useEffect(() => {
    if (results.length === 0) {
      setIdentificationId("");
      return;
    }
    if (!results.some((r) => String(r.identificationId) === identificationId)) {
      setIdentificationId(String(results.slice().sort((a, b) => a.studentRoll - b.studentRoll)[0].identificationId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const student = results.find((r) => String(r.identificationId) === identificationId);
  const studentMarks = marks.filter((m) => String(m.identificationId) === identificationId).sort((a, b) => a.subjectName.localeCompare(b.subjectName));

  const sortedByRoll = useMemo(() => results.slice().sort((a, b) => a.studentRoll - b.studentRoll), [results]);

  return (
    <MainLayout>
      <div className="page-header no-print">
        <h1>Marksheet</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters no-print">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Student
          <select value={identificationId} onChange={(e) => setIdentificationId(e.target.value)}>
            {sortedByRoll.map((r) => (
              <option key={r.identificationId} value={r.identificationId}>
                {r.studentRoll} — {r.studentName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-secondary" onClick={() => window.print()} disabled={!student}>
          Print
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : !student ? (
        <p>No results found for this selection.</p>
      ) : (
        <div className="marksheet-card">
          <div className="marksheet-header">
            <div>
              <strong>{student.studentName}</strong> ({student.customStudentId})
            </div>
            <div>
              Roll: {student.studentRoll} &nbsp;|&nbsp; Section: {student.sectionName} &nbsp;|&nbsp; Shift: {student.shiftName}
              {student.groupName && student.groupName !== "N/A" ? ` | Group: ${student.groupName}` : ""}
            </div>
          </div>

          <table className="attendance-table marksheet-subject-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Obtained</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {studentMarks.map((m) => (
                <tr key={m.subjectId}>
                  <td>{m.subjectName}</td>
                  <td>{m.totalScore}</td>
                  <td>{m.totalMark}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="marksheet-summary">
            <div>
              <span className="marksheet-summary-label">Total Obtained</span>
              <span className="marksheet-summary-value">{student.obtainMark}</span>
            </div>
            <div>
              <span className="marksheet-summary-label">Grade</span>
              <span className="marksheet-summary-value">{student.letterGrade}</span>
            </div>
            <div>
              <span className="marksheet-summary-label">GPA</span>
              <span className="marksheet-summary-value">{student.avgGpaWithoutOptional}</span>
            </div>
            <div>
              <span className="marksheet-summary-label">Result</span>
              <span className="marksheet-summary-value">{student.resultStatus}</span>
            </div>
            <div>
              <span className="marksheet-summary-label">Position</span>
              <span className="marksheet-summary-value">{student.classPosition === 0 ? "—" : student.classPosition}</span>
            </div>
          </div>
          {student.remark && (
            <div className="marksheet-remark">
              <strong>Remarks:</strong> {student.remark}
            </div>
          )}
        </div>
      )}
    </MainLayout>
  );
}
